<script lang="ts">
import {defineComponent, PropType, ref, toRefs, watch} from '@vue/composition-api'
import {TranslateResult} from 'vue-i18n'

export default defineComponent({
  name: 'Tooltip',
  props: {
    text: {
      type: String as PropType<string | TranslateResult>,
      required: true,
    },
    position: {
      type: String as PropType<'top' | 'bottom' | 'left' | 'right'>,
      default: 'top',
    },
    width: {
      type: Number,
      default: 200,
    },
    attach: {
      type: String,
      default: undefined,
    },
  },
  setup(props, {refs}) {
    const attachmentStyles = ref('')
    const isHovering = ref(false)

    const {attach: attachLocation} = toRefs(props)

    watch(isHovering, getAttachmentStyles)

    function getAttachmentStyles(isHovering: boolean) {
      if (!attachLocation.value || !isHovering) {
        attachmentStyles.value = ''
        return
      }

      const content = refs.content as HTMLDivElement
      const {top, left, right, bottom, height, width} = content.getBoundingClientRect()

      const horizontalOffset = () => {
        const padding = 12

        if (props.position === 'right') {
          return `left: ${left + padding + width}px;`
        }
        if (props.position === 'left') {
          return `right: ${right + padding + width}px;`
        }
        return ''
      }
      const verticalOffset = () => {
        if (props.position === 'top') {
          return `bottom: ${bottom + height * 0.25}px;`
        }
        if (props.position === 'bottom') {
          return `top: ${top + height * 0.25}px;`
        }
        if (props.position === 'left' || props.position === 'right') {
          return `top: ${top}px;`
        }
        return ''
      }

      attachmentStyles.value = `
        position: absolute;
        ${verticalOffset()}
        ${horizontalOffset()}
        --position: ${props.position};
        --reverse-position: ${reversePosition()};
        z-index: 1;
      `
    }

    function reversePosition() {
      if (props.position === 'top') {
        return 'bottom'
      }
      if (props.position === 'bottom') {
        return 'top'
      }
      if (props.position === 'left') {
        return 'right'
      }
      if (props.position === 'right') {
        return 'left'
      }
      return ''
    }

    return {attachmentStyles, isHovering, attachLocation, reversePosition}
  },
})
</script>

<template>
  <div class="tooltip" :class="position" :style="`--width: ${width}px`">
    <div class="tooltip__content" ref="content" @mouseover="isHovering = true" @mouseleave="isHovering = false">
      <slot />
    </div>
    <portal :to="attach || ''" :disabled="!attach">
      <div
        class="tooltip__text"
        :class="[position, isHovering ? 'hover' : '']"
        :style="attach ? attachmentStyles : `--position: ${position}; --reverse-position: ${reversePosition()};`">
        {{ text }}
      </div>
    </portal>
  </div>
</template>
<style lang="scss">
.vue-portal-target {
  display: flex;
  opacity: 0.95;
  z-index: 1;

  .tooltip__text {
    --height: 40px;

    width: var(--width);
    height: var(--height);

    position: absolute;
    display: none;
    justify-content: center;
    align-items: center;
    padding: 0 12px;

    text-align: center;
    border-radius: 4px;

    font-family: 'ProximaNovaRegular', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.5;

    background-color: hsl(0 0 23%);
    color: hsl(0 0 100%);
    z-index: 1;

    &.hover {
      animation-name: fadeIn;
      animation-duration: 0.12s;
      animation-timing-function: ease-in-out;
      transform-origin: var(--reverse-position) center;
      display: flex;
    }

    &::after {
      content: '';
      position: absolute;
      width: 28px;
      height: 28px;
      border-radius: 4px;
      background-color: hsl(0 0 23%);
      z-index: -1;
    }

    &.left {
      &::after {
        top: 50%;
        transform: translateY(-50%) rotate(45deg);
        right: -4px;
      }
    }

    &.right {
      &::after {
        top: 50%;
        transform: translateY(-50%) rotate(45deg);
        left: -4px;
      }
    }

    &.top {
      width: var(--width);
      bottom: 100%;
      left: 50%;
      margin-left: calc((var(--width) / 2) * -1);
      margin-bottom: 2px;

      &::after {
        left: 50%;
        transform: translateX(-50%) rotate(45deg);
        bottom: -4px;
      }
    }

    &.bottom {
      width: var(--width);
      top: 100%;
      left: 50%;
      margin-left: calc((var(--width) / 2) * -1);

      &::after {
        left: 50%;
        transform: translateX(-50%) rotate(45deg);
        top: -4px;
      }
    }
  }
}
</style>
<style lang="scss" scoped>
/* Tooltip text */
.tooltip {
  --width: 200px;
  --height: 40px;

  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  opacity: 0.95;

  .tooltip__content {
    width: 100%;
    height: 100%;
  }

  .tooltip__text {
    position: absolute;
    display: none;
    justify-content: center;
    align-items: center;
    width: var(--width);
    height: var(--height);
    user-select: none;

    text-align: center;
    border-radius: 4px;

    font-family: 'ProximaNovaRegular', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.5;

    background-color: hsl(0 0 23%);
    color: hsl(0 0 100%);
    z-index: 1;

    &.hover {
      animation-name: fadeIn;
      animation-duration: 0.12s;
      animation-timing-function: ease-in-out;
      transform-origin: var(--reverse-position) center;
      display: flex;
    }
  }

  &.left {
    .tooltip__text {
      width: var(--width);
      top: 50%;
      right: 100%;
      margin-left: calc((var(--width) / 2) * -1);

      &::after {
        top: 50%;
        transform: translateY(-50%) rotate(45deg);
        right: -4px;
      }
    }
  }

  &.right {
    .tooltip__text {
      width: var(--width);
      top: 50%;
      left: 100%;
      margin-left: calc((var(--width) / 2) * -1);

      &::after {
        top: 50%;
        transform: translateY(-50%) rotate(45deg);
        left: -4px;
      }
    }
  }

  &.top {
    .tooltip__text {
      width: var(--width);
      bottom: 100%;
      left: 50%;
      margin-left: calc((var(--width) / 2) * -1);
      margin-bottom: 2px;

      &::after {
        left: 50%;
        transform: translateX(-50%) rotate(45deg);
        bottom: -4px;
      }
    }
  }

  &.bottom {
    .tooltip__text {
      width: var(--width);
      top: 100%;
      left: 50%;
      margin-left: calc((var(--width) / 2) * -1);

      &::after {
        left: 50%;
        transform: translateX(-50%) rotate(45deg);
        top: -4px;
      }
    }
  }

  .tooltip__text::after {
    content: '';
    position: absolute;
    width: 28px;
    height: 28px;
    border-radius: 4px;
    background-color: hsl(0 0 23%);
    z-index: -1;
  }
}

@keyframes zoomIn {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
